import * as React from 'react';
import { resolve } from 'inversify-react';
import { Button, Table, Input, Breadcrumb } from 'antd';
import { ColumnProps, TableRowSelection, TableComponents, TableProps } from 'antd/lib/table/';
import { PaginationProps } from 'antd/lib/pagination/Pagination';
import { observable, isArrayLike } from 'mobx';
import { observer, Observer } from 'mobx-react';
import autobind from 'autobind-decorator';

import { ToastNotifications } from 'src/core/ui/toast';
import { ObjectUtils } from 'src/core/utils/object';
import { DialogService } from 'src/core/services/ui/dialog.service';
import { FilterEditorView, FilterEditorType } from 'src/core/ui/filter-editor.view';
import { ZuluIcon } from 'src/app/shared/icon';

export interface Column {
	key: string;
	title: React.ReactNode;
	dataIndex?: string;
	width?: string | number;
	fixed?: boolean | ('left' | 'right');
	render?: (text: any, record: any, index: number) => React.ReactNode;
	children?: Column[];
	sortable?: boolean;
	filter?: {
		field: string,
		type: FilterEditorType,
		render: (value: any, onChange: (newValue: any) => void) => React.ReactNode
	};
	editable?: (text: any, record: any, index: number, isSelected: boolean, refresh?: () => void) => React.ReactNode;
	className?: string;
	colSpan?: number;
	footer?: (columnValues: any[], currentPage: any[]) => React.ReactNode;
}

export interface MenuItem {
	key: string;
	name: string;
	icon: string;
	disabled?: boolean;
	onClick?: (e: any) => void;
}

export interface DataTableProps extends ComponentViewProps<DataSet<any>> {
	columns: Column[];
	afterDoubleClick?: (item: any) => void;
	itemDoubleClick?: (item: any) => void;
	createNewItem?: () => Promise<any>;
	afterCreateNewItem?: (item: any) => void;
	beforeDeleteItem?: (item: any[]) => any;
	afterDeleteItem?: (items: any[]) => void;
	afterRestoreItem?: (items: any[]) => void;
	hideToolbar?: ToolbarVisibility;
	unselectable?: boolean;
	hierarchyId?: string[];
	hierarchyParent?: string[];
	hierarchyHasChildren?: (item: any) => boolean;
	toolbarLeftItems?: MenuItem[] | React.ReactNode;
	mode?: ControlMode;
	rowKey?: ((record: any) => string) | string;
	expandedRowRender?: any;
	isValidItem?: (entity: any) => Promise<React.ReactNode[]>;
	selection?: TableRowSelection<any> | false;
	rowClassName?: (record: any, index: number) => string;
	components?: TableComponents;
	onCancel?: () => Promise<any>;
	otherProps?: Partial<TableProps<any>>;
	clientFilter?: (record: any) => boolean;
	applyParamsFilter?: string[];
}

export enum ToolbarVisibility {
	hideSearch = 1,
	hideModifyButtons = 2,
	hidePagination = 4,
	hideFooter = 8,
}

interface BreadcrumbItem {
	item: any;
	text: any;
	breadcrumbName: string;
	onClick: () => void;
	path: string;
}

interface DataTableState {
	q?: string;
	selectionCount: number;
	selectedRows?: Object[];
	edition?: any;
	isArchived: boolean;
	filterVisible: { [key: string]: boolean };
	breadcrumb: BreadcrumbItem[];
	count: number;
	filters: {
		[field: string]: { operator: FilterOperator, value: any }
	};
}

@observer
export class DataTableView extends React.Component<DataTableProps, DataTableState> {
	@observable
	mode: ControlMode = 'display';

	@resolve
	private dialogService: DialogService;

	@resolve
	protected toast: ToastNotifications;

	private sorts: SortingModel = {};

	@autobind
	private rowKey(record: any): string {
		if (this.rowKey) {
			if (typeof this.props.rowKey == 'function') {
				return this.props.rowKey(record);
			} else if (typeof this.props.rowKey == 'string') {
				return record[this.props.rowKey];
			}
		}
		return record['id'];
	}

	private get isFiltering(): boolean {
		return (this.state.q != null && this.state.q != '') || Object.getOwnPropertyNames(this.state.filterVisible).length > 0;
	}

	constructor(props: any) {
		super(props);

		if (this.props.hierarchyParent) {
			for (let parentName of this.props.hierarchyParent) {
				this.props.model.addFilter(parentName, 'eq', null);
			}
		}
		const filterableColumns = props.columns.filter(m => m.filter);
		this.state = {
			selectionCount: 0,
			filterVisible: {},
			isArchived: false,
			breadcrumb: [],
			count: 10,
			filters: Object.assign({}, ...filterableColumns.map((m) => ({
				[m.filter.field]: { operator: 'eq' }
			})))
		};
	}

	@autobind
	private catchFunc(error) {
		this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
		this.props.setBusy(false);
	};

	@autobind
	private run(func: () => Promise<any>) {
		this.props.setBusy(true);
		func().then(result => {
			this.props.setBusy(false);
			// TODO: Clear selection?
		}).catch(this.catchFunc);
	}

	private updateHierarchyfilter(item) {
		if (item != null) {
			for (let i = 0; i < this.props.hierarchyParent.length; i++) {
				this.props.model.addFilter(this.props.hierarchyParent[i], undefined, item[this.props.hierarchyId[i]]);
			}
		} else {
			for (let i = 0; i < this.props.hierarchyParent.length; i++) {
				this.props.model.addFilter(this.props.hierarchyParent[i], undefined, null);
			}
		}
		this.props.model.currentPage = 1;
		this.reload(true);
	}

	private renderBreadcrumbItem(route: any, params: any, routes: Array<any>, paths: Array<string>) {
		return <Button key={route.breadcrumbName} type='dashed' onClick={() => route.onClick(route.breadcrumbName)}>
			{route.text}
			<ZuluIcon type='close' />
		</Button>;
	}

	@autobind
	private closeItem(key: string) {
		const index = this.state.breadcrumb.findIndex(item => item.breadcrumbName == key);
		if (index != -1) {
			if (index > 0)
				this.updateHierarchyfilter(this.state.breadcrumb[index - 1].item);
			else
				this.updateHierarchyfilter(null);
			this.state.breadcrumb.splice(index);
			this.setState({ breadcrumb: this.state.breadcrumb });
		}
	}

	private expandItem(value: any) {
		const breadcrumb = this.state.breadcrumb;
		let title = value.name ? value.name : value.title ? value.title : value.displayName;
		breadcrumb.push({ text: title, breadcrumbName: title, onClick: () => { this.closeItem(title) }, item: value, path: null });
		this.updateHierarchyfilter(value);
		this.setState({
			breadcrumb: breadcrumb,
		});
	}

	componentWillReceiveProps(nextProps): void {
		if (nextProps.mode != this.props.mode && nextProps.mode == 'display') {
			this.setState({ edition: null });
		}
	}

	private getTableComponents(): TableComponents {
		const { columns, selection, expandedRowRender, model } = this.props;
		let components = {};

		if (columns.some(m => m.footer != null)) {
			components = {
				table: (props) => {
					let columnsInFooter = columns;
					if (props.className === 'ant-table-fixed' && props.style.width == null) {
						const index = columns.findIndex(m => m.fixed !== 'left');
						columnsInFooter = columns.slice(0, index !== -1 ? index : columns.length);
					}

					return (
						<table {...props}>
							<React.Fragment>
								{props.children}
								<tfoot className="ant-table-tfooter">
									<tr>
										{selection !== false && <th />}
										{expandedRowRender != null && <th />}
										{...columnsInFooter.map(m => {
											if (m.footer != null) {
												const currentPage = model.items.map(m => m);
												return (
													<th
														key={m.key}
														colSpan={m.children != null ? m.children.length : 1}>
														{m.footer(currentPage.map(p => p[m.dataIndex]), currentPage)}
													</th>
												);
											} else {
												return (
													<th
														key={m.key}
														colSpan={m.children != null ? m.children.length : 1}
													/>
												);
											}
										})}
									</tr>
								</tfoot>
							</React.Fragment>
						</table>
					);
				}
			};
		}

		return { ...components, ...this.props.components };
	}

	render(): JSX.Element {
		if (this.props.columns == null) {
			return null;
		}

		const model = this.props.model;
		let items = model.items.map(o => o);
		const columns: ColumnProps<any>[] = this.configureColumns(this.props.columns);
		if (this.props.hierarchyParent) {
			const breadcrumb: ColumnProps<any> = {
				key: 'breadcrumb',
				title: '',
				dataIndex: 'breadcrumb',
				width: 50
			};
			columns.unshift(breadcrumb);

			items = items.map((item) => {
				if (this.props.hierarchyHasChildren == null || this.props.hierarchyHasChildren(item)) {
					item.breadcrumb = <ZuluIcon type='right' onClick={() => this.expandItem(item)} className='cursor' />;
				}
				return item;
			});
		}

		const hasNoTitle = this.state.breadcrumb.length === 0 && this.props.toolbarLeftItems == null &&
			this.props.hideToolbar != null && (this.props.hideToolbar & ToolbarVisibility.hideModifyButtons) === ToolbarVisibility.hideModifyButtons &&
			(this.props.hideToolbar & ToolbarVisibility.hideSearch) === ToolbarVisibility.hideSearch;

		return (
			<Table
				className={this.props.model.isDirty ? 'is-dirty' : ''}
				rowKey={this.rowKey}
				rowSelection={this.onRowSelection()}
				title={hasNoTitle ? undefined : this.renderHeader}
				columns={columns}
				dataSource={this.props.clientFilter ? items.filter(this.props.clientFilter) : items}
				pagination={(this.props.hideToolbar & ToolbarVisibility.hidePagination) != ToolbarVisibility.hidePagination ? {
					current: model.currentPage,
					pageSize: model.pageSize,
					total: model.count,
					showSizeChanger: true,
					onShowSizeChange: this.onShowSizeChange,
					showQuickJumper: false,
					showTotal: this.onTotal
				} : false}
				onChange={this.onRequest}
				onRow={(record, index) => ({
					onDoubleClick: () => this.onEdit(record, index),
				})}
				expandedRowRender={this.props.expandedRowRender}
				rowClassName={this.props.rowClassName}
				size="small"
				components={this.getTableComponents()}
				{...this.props.otherProps}
			/>
		);
	}
	@autobind
	onShowSizeChange(current, pageSize) {
		this.props.model.pageSize = pageSize;
		this.reload(true);
	}
	@autobind
	onChangeFilter(field, cfg: { operator?: FilterOperator, value?: any }) {
		const filter = this.state.filters[field];
		const operator = 'operator' in cfg ? cfg.operator : filter.operator;
		const value = 'value' in cfg ? cfg.value : filter.value;
		this.props.model.currentPage = 1;
		if (value == null || ((typeof value) === 'string' && value == '')) {
			if (this.props.applyParamsFilter != null && this.props.applyParamsFilter.find(x => x == field) != null) {
				delete (this.props.model as any).params[field];
			}
			else {
				this.props.model.removeFilter(field);
			}

		} else {
			if (this.props.applyParamsFilter != null && this.props.applyParamsFilter.find(x => x == field) != null) {
				(this.props.model as any).params[field] = JSON.stringify({ value: value, operator: operator });
			}
			else {
				this.props.model.addFilter(field, operator, value);
			}

		}
		this.props.model.reload();

		this.setState({
			filters: Object.assign({}, this.state.filters, {
				[field]: { operator, value }
			})
		});
	}

	private configureColumns(columns: Column[]) {
		const result: ColumnProps<any>[] = [];
		for (const column of columns) {
			let col: ColumnProps<any> = {
				key: column.key,
				title: column.title,
				dataIndex: column.dataIndex,
				render: column.render,
				children: column.children ? this.configureColumns(column.children) : undefined,
				className: column.className + (this.props.model.isDirty ? ' is-dirty' : ''),
				colSpan: column.colSpan,
				width: column.width,
				fixed: column.fixed
			};

			if (column.sortable) {
				col.sorter = true;
			}

			if (column.filter) {
				const { filter } = column;
				const options = this.state.filters[filter.field];

				col.filterDropdown = (
					<FilterEditorView
						filterType={filter.type}
						operatorCheck={(operator: FilterOperator) => options.operator === operator}
						onClick={(operator: FilterOperator) => this.onChangeFilter(filter.field, { operator })}>
						{
							filter.render(options.value, (value) => this.onChangeFilter(filter.field, { value }))
						}
					</FilterEditorView>
				);
				col.filterDropdownVisible = this.state.filterVisible[column.dataIndex];
				col.onFilterDropdownVisibleChange = (visible: boolean) => {
					const filterVisible = this.state.filterVisible;
					filterVisible[col.dataIndex] = visible;
					this.setState({
						filterVisible: filterVisible
					});
				}
			}
			if (column.editable) {
				col.render = (text: any, record: any, index: number) => {
					const isSelected = this.props.mode == 'edit' && this.state.edition && this.rowKey(this.state.edition) == this.rowKey(record);
					const refresh = () => this.setState({ edition: record });

					return <Observer>{() => column.editable(text, record, index, isSelected, refresh)}</Observer>;
				};
			}

			result.push(col);
		}
		return result;
	}

	@autobind
	private onRequest(pagination: PaginationProps, filters: any, sorter: any) {
		this.props.model.currentPage = pagination.current;

		for (const prop in this.sorts) {
			delete this.sorts[prop];
			this.props.model.removeSorting(prop);
		}

		if (sorter.field) {
			this.sorts[sorter.field] = {
				dir: sorter.order == 'ascend' ? 'asc' : 'desc'
			};
			this.props.model.addSorting(sorter.field, this.sorts[sorter.field].dir);
		}
		this.reload(true);
	}

	@autobind
	private reload(skipHierarchyCheck?: boolean) {
		this.props.setBusy(true);
		setTimeout(() => {
			if (!skipHierarchyCheck && this.props.hierarchyParent) {
				for (let parentName of this.props.hierarchyParent) {
					if (this.isFiltering) {
						this.props.model.removeFilter(parentName);
					} else {
						this.props.model.addFilter(parentName, 'eq', null);
					}
				}
			}
			this.props.model.reload()
				.then(() => { this.props.setBusy(false); });
		});
	}

	@autobind
	private onRowSelection() {
		if (this.props.selection === false) {
			return undefined;
		}
		let selection = {
			onChange: (selectedRowKeys: string[] | number[], selectedRows: Object[]) => {
				this.setState({
					selectionCount: selectedRows.length,
					selectedRows: selectedRows
				});
				if (this.props.selection && this.props.selection.onChange) {
					this.props.selection.onChange(selectedRowKeys, selectedRows);
				}
			},
			selectedRowKeys: this.state.selectedRows
				? this.state.selectedRows.map(record => this.rowKey(record))
				: [],
		};
		if (this.props.selection) {
			let res = ObjectUtils.deepCopy(this.props.selection);
			res.onChange = selection.onChange;
			selection = res;
		}
		return selection;
	}

	@autobind
	private renderHeader() {
		const customHeader = this.props.toolbarLeftItems
			? isArrayLike(this.props.toolbarLeftItems)
				? <Button.Group className={(this.props.model.isModelDirty ? "dirty " : "") + "ml-3"}>
					{
						(this.props.toolbarLeftItems as MenuItem[]).map(e => {

							let icon = e.icon && e.icon.search("^icon-") !== -1
								? <ZuluIcon type={e.icon} />
								: undefined;
							return (
								<Button
									className='editable-add-btn'
									icon={icon ? undefined : e.icon}
									disabled={e.disabled}
									onClick={e.onClick}
									key={e.key}
								>
									{icon}{e.name}
								</Button>
							);
						}
						)
					}
				</Button.Group>
				: this.props.toolbarLeftItems
			: undefined;

		const search = (this.props.hideToolbar & ToolbarVisibility.hideSearch) != ToolbarVisibility.hideSearch
			? (
				<Input.Search
					autoFocus
					onChange={e => this.setState({
						q: e.currentTarget.value
					})}
					onSearch={this.onSearch}
					onPressEnter={this.onSearch}
					placeholder='Search'
					value={this.state.q}
					style={{ width: '300px', marginRight: '10px' }}
					enterButton={true}
				/>
			)
			: undefined;
		const modify =
			(this.props.hideToolbar & ToolbarVisibility.hideModifyButtons) != ToolbarVisibility.hideModifyButtons
				? (
					<Button.Group className={this.props.model.isModelDirty ? "dirty" : ""}>
						<Button icon="plus"
							onClick={this.onAdd}
							disabled={this.props.mode == 'display'}
						>
							Add
								</Button>
						{
							this.state.edition &&
							<Button icon="close"
								onClick={this.onClose}
								disabled={this.props.mode == 'display'}
							>
								Cancel
										</Button>
						}
						{
							!this.state.isArchived &&
							<Button icon="delete"
								onClick={this.onDelete}
								disabled={this.props.mode == 'display' || this.state.selectionCount == 0}
							>
								Delete
										</Button>
						}
						{
							this.state.isArchived &&
							<Button icon="undo"
								onClick={this.onRestore}
								disabled={this.props.mode == 'display' || this.state.selectionCount == 0 || this.props.model.isModelDirty}
							>
								Restore
										</Button>
						}
						{
							this.props.model.hasFilter('isDeleted') &&
							<Button icon="rest"
								type={this.state.isArchived ? 'primary' : undefined}
								disabled={this.props.mode == 'display' || this.props.model.isModelDirty}
								onClick={() => {
									this.props.model.currentPage = 1;
									this.props.model.addFilter('isDeleted', 'eq', !this.state.isArchived);
									this.setState({
										isArchived: !this.state.isArchived
									});
									this.reload(true);
								}}
							>
								Archived
										</Button>
						}
						<Button
							icon="reload"
							onClick={() => { this.reload(true); }}
							disabled={this.props.mode == 'display' || this.props.model.isModelDirty}
						>
							Reload
									</Button>
					</Button.Group>
				)
				: undefined;
		const breadcrumb = (
			<Breadcrumb
				className='m1'
				separator={<ZuluIcon type='right' />}
				itemRender={this.renderBreadcrumbItem}
				routes={this.state.breadcrumb}
			/>
		);
		return (
			<React.Fragment>
				{search}
				{modify}
				{customHeader}
				{breadcrumb}
			</React.Fragment>
		);
	}

	@autobind
	private onSearch() {
		this.props.model.searchKeywords = this.state.q;
		this.props.model.currentPage = 1;
		this.reload();
	}

	@autobind
	private async onAdd() {
		let errors = await this.isItemInEditionValid();
		if (errors.length > 0) {
			this.toast.showToast('Request failed', errors, 'error');
			return;
		}
		if (this.props.createNewItem) {
			this.props.createNewItem()
				.then(e => {
					if (this.props.afterCreateNewItem) {
						this.props.afterCreateNewItem(e);
					}
					this.setState({ edition: e });
				});
		} else if (this.props.itemDoubleClick) {
			this.props.itemDoubleClick(null);

			this.setState({
				edition: this.props.model.items.slice()[this.props.model.items.length],
			});
		}
	}

	@autobind
	private async onEdit(record: any, index: number) {
		let errors = await this.isItemInEditionValid();
		if (errors.length > 0) {
			this.toast.showToast('Request failed', errors, 'error');
			return;
		}
		this.setState({
			edition: record
		});
		if (this.props.itemDoubleClick) {
			this.props.itemDoubleClick(record);
		}
		if (this.props.afterDoubleClick) {
			this.props.afterDoubleClick(record);
		}
	}

	@autobind
	private onDelete() {
		this.dialogService.showYesNo('Delete element', 'Are you sure?')
			.then((dialogResult) => {
				if (dialogResult) {
					if (this.state.selectionCount > 0) {
						let items = this.state.selectedRows;
						if (this.props.beforeDeleteItem) {
							items = this.props.beforeDeleteItem(items);
						}

						if (items) {
							this.props.model.currentPage = (items.length == this.props.model.items.length) ? 1 : this.props.model.currentPage;
							this.run(() => {
								return this.props.model.delete(items)
									.then(() => this.props.afterDeleteItem && this.props.afterDeleteItem(items))
									.then(() => {
										this.setState({
											selectionCount: 0,
											selectedRows: [],
											edition: null
										});
									})
									.catch(error => this.toast.showToast('Delete failed!', ObjectUtils.parseError(error), 'error'));
							});
						}
					}
				}
			});
	}

	@autobind
	private onRestore() {
		this.dialogService.showYesNo('Restore element', 'Are you sure?')
			.then((dialogResult) => {
				if (dialogResult) {
					if (this.state.selectionCount > 0) {
						const items = this.state.selectedRows;

						this.run(() => {
							return this.props.model.restore(items)
								.then(() => this.props.afterRestoreItem && this.props.afterRestoreItem(items))
								.then(() => {
									this.setState({
										selectionCount: 0,
										selectedRows: [],
									});
								})
								.catch(error => this.toast.showToast('Restore failed!', ObjectUtils.parseError(error), 'error'));
						});
					}
				}
			});
	}

	@autobind
	private onTotal(total: number, range: [number, number]) {
		return `${range[0]}-${range[1]} of ${total} items`;
	}

	@autobind
	async onClose() {

		if (this.props.onCancel)
			this.props.onCancel();
		let errors = await this.isItemInEditionValid()
		if (errors.length > 0) {
			this.toast.showToast('Request failed', errors, 'error');
			return;
		}
		this.setState({
			edition: undefined
		})
	}

	private async isItemInEditionValid(): Promise<string[]> {
		this.props.setBusy(true);
		const { edition } = this.state;
		let errors = [];
		if (edition != null) {
			if (this.props.isValidItem == null && edition.validate) {
				const valError = await (edition as Model).validate(true);
				errors = valError.map(x => x.text);
			} else if (this.props.isValidItem) {
				errors = await this.props.isValidItem(edition);
			}
		}
		this.props.setBusy(false);
		return errors;
	}
}



// WEBPACK FOOTER //
// ./src/core/ui/data-table.view.tsx